import { useState, useEffect, useCallback } from 'react';
export const useProducts = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await fetch('/data/products.json', {
          // Cache for 5 minutes to avoid excessive requests
          cache: 'default'
        });
        
        if (!response.ok) {
          throw new Error(`Failed to load products: ${response.status} ${response.statusText}`);
        }
        
        const data = await response.json();
        
        // Validate data structure
        if (!data || !Array.isArray(data.products)) {
          throw new Error('Invalid products data format');
        }
        
        setProducts(data.products);
        setError(null);
        
        if (process.env.NODE_ENV === 'development') {
        }
      } catch (err) {
        console.error('❌ Error loading products:', err);
        setError(err.message);
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []); // Empty dependency array - fetch once on mount

  /**
   * Get a single product by ID
   * @param {string|number} id - Product ID
   * @returns {Object|undefined} Product object or undefined if not found
   */
  const getProductById = useCallback((id) => {
    return products.find(p => p.id === parseInt(id));
  }, [products]);

  /**
   * Get products filtered by category and optional subcategory
   * @param {string} category - Category slug (e.g., 'dressers')
   * @param {string|null} subcategory - Optional subcategory slug
   * @returns {Array} Filtered products array
   */
  const getProductsByCategory = useCallback((category, subcategory = null) => {
    return products.filter(p => {
      const categoryMatch = p.category?.toLowerCase() === category?.toLowerCase();
      
      if (subcategory) {
        const subcategoryMatch = p.subcategory?.toLowerCase() === subcategory?.toLowerCase();
        return categoryMatch && subcategoryMatch;
      }
      
      return categoryMatch;
    });
  }, [products]);

  /**
   * Get only in-stock products
   * @returns {Array} In-stock products array
   */
  const getInStockProducts = useCallback(() => {
    return products.filter(p => p.inStock === true);
  }, [products]);

  /**
   * Get out-of-stock products
   * @returns {Array} Out-of-stock products array
   */
  const getOutOfStockProducts = useCallback(() => {
    return products.filter(p => p.inStock === false);
  }, [products]);

  /**
   * Search products by query string
   * Searches across name, category, subcategory, and description
   * @param {string} query - Search query
   * @returns {Array} Matching products array
   */
  const searchProducts = useCallback((query) => {
    if (!query || query.trim() === '') {
      return products;
    }

    const searchLower = query.toLowerCase().trim();
    const searchWords = searchLower.split(/\s+/).filter(word => word.length > 0);

    return products.filter(p => {
      const searchableText = [
        p.name || '',
        p.category || '',
        p.subcategory || '',
        p.description || ''
      ].join(' ').toLowerCase();
      
      const normalizedText = searchableText.replace(/-/g, ' ');
      
      // Check if ALL search words are found
      const allWordsMatch = searchWords.every(word => 
        normalizedText.includes(word) || searchableText.includes(word)
      );
      
      if (allWordsMatch) {
        return true;
      }
      
      // Fallback: check if entire phrase matches
      return normalizedText.includes(searchLower) || searchableText.includes(searchLower);
    });
  }, [products]);

  /**
   * Get products sorted by various criteria
   * @param {Array} productsList - Products array to sort (defaults to all products)
   * @param {string} sortBy - Sort criteria: 'price-low', 'price-high', 'name-asc', 'name-desc', 'newest', 'oldest'
   * @returns {Array} Sorted products array
   */
  const getSortedProducts = useCallback((productsList = products, sortBy = 'newest') => {
    const sorted = [...productsList];

    switch (sortBy) {
      case 'price-low':
        return sorted.sort((a, b) => (a.priceCAD || 0) - (b.priceCAD || 0));
      
      case 'price-high':
        return sorted.sort((a, b) => (b.priceCAD || 0) - (a.priceCAD || 0));
      
      case 'name-asc':
        return sorted.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      
      case 'name-desc':
        return sorted.sort((a, b) => (b.name || '').localeCompare(a.name || ''));
      
      case 'oldest':
        return sorted.sort((a, b) => a.id - b.id);
      
      case 'newest':
      default:
        return sorted.sort((a, b) => b.id - a.id);
    }
  }, [products]);

  /**
   * Get products within a price range
   * @param {number} minPrice - Minimum price (inclusive)
   * @param {number} maxPrice - Maximum price (inclusive)
   * @returns {Array} Filtered products array
   */
  const getProductsByPriceRange = useCallback((minPrice, maxPrice) => {
    return products.filter(p => {
      const price = p.priceCAD || 0;
      return price >= minPrice && price <= maxPrice;
    });
  }, [products]);

  /**
   * Get unique categories from all products
   * @returns {Array} Array of unique category strings
   */
  const getCategories = useCallback(() => {
    const categories = products
      .map(p => p.category)
      .filter(Boolean) // Remove null/undefined
      .filter((v, i, arr) => arr.indexOf(v) === i); // Unique values
    
    return categories.sort();
  }, [products]);

  /**
   * Get unique subcategories for a specific category
   * @param {string} category - Category slug
   * @returns {Array} Array of unique subcategory strings
   */
  const getSubcategories = useCallback((category) => {
    const subcategories = products
      .filter(p => p.category?.toLowerCase() === category?.toLowerCase())
      .map(p => p.subcategory)
      .filter(Boolean)
      .filter((v, i, arr) => arr.indexOf(v) === i);
    
    return subcategories.sort();
  }, [products]);

  /**
   * Get similar products based on category, style, room, or material
   * @param {Object} product - Reference product
   * @param {number} limit - Maximum number of similar products to return
   * @returns {Array} Similar products array
   */
  const getSimilarProducts = useCallback((product, limit = 8) => {
    if (!product) return [];

    return products
      .filter(p => 
        p.id !== product.id && 
        (
          p.category === product.category ||
          p.style === product.style ||
          p.room === product.room ||
          p.material === product.material
        )
      )
      .slice(0, limit);
  }, [products]);

  /**
   * Get featured products (you can customize the logic)
   * Currently returns newest 4 products
   * @param {number} limit - Maximum number of featured products
   * @returns {Array} Featured products array
   */
  const getFeaturedProducts = useCallback((limit = 4) => {
    return getSortedProducts(products, 'newest').slice(0, limit);
  }, [products, getSortedProducts]);

  /**
   * Get products count by category
   * @returns {Object} Object with category as key and count as value
   */
  const getProductCountByCategory = useCallback(() => {
    const counts = {};
    products.forEach(p => {
      if (p.category) {
        counts[p.category] = (counts[p.category] || 0) + 1;
      }
    });
    return counts;
  }, [products]);

  // Return all state and helper functions
  return {
    // State
    products,
    loading,
    error,
    
    // Single product
    getProductById,
    
    // Filtering
    getProductsByCategory,
    getInStockProducts,
    getOutOfStockProducts,
    getProductsByPriceRange,
    
    // Search
    searchProducts,
    
    // Sorting
    getSortedProducts,
    
    // Categories
    getCategories,
    getSubcategories,
    getProductCountByCategory,
    
    // Related products
    getSimilarProducts,
    getFeaturedProducts,
    
    // Convenience
    totalProducts: products.length,
    hasProducts: products.length > 0
  };
};

export default useProducts;